import * as React from 'react';

import { Skeleton, SkeletonProps, Table } from 'antd';
import type { TableColumnsType as ColumnsType } from 'antd';

export type SkeletonTableColumnsType = {
  key: string;
};

type SkeletonTableProps = SkeletonProps & {
  columns: ColumnsType<SkeletonTableColumnsType>;
  rowCount?: number;
  children?: React.ReactNode;
};

/**
 * If the child is an antd Table with an empty dataSource, clone it and force
 * `pagination={false}` so the pagination bar is hidden when there are no rows.
 * Any other child (or a Table with data) is returned unchanged.
 *
 * A non-zero `total` means the list itself is not empty and the current page is
 * simply past the end — keep the pager so the user can navigate back.
 */
const hidePaginationWhenEmpty = (child: React.ReactNode): React.ReactNode => {
  if (React.isValidElement(child) && child.type === Table) {
    const { dataSource, pagination } = child.props as any;
    const total =
      pagination && typeof pagination === "object"
        ? pagination.total
        : undefined;
    if ((!dataSource || dataSource.length === 0) && !total) {
      return React.cloneElement(child as React.ReactElement<any>, {
        pagination: false,
      });
    }
  }
  return child;
};

export default function SkeletonTable({
  loading = false,
  active = true,
  rowCount = 5,
  columns,
  children,
  className,
}: SkeletonTableProps): JSX.Element {
  return loading ? (
    <Table
      rowKey="key"
      pagination={false}
      dataSource={[...Array(rowCount)].map((_, index) => ({
        key: `key${index}`,
      }))}
      columns={columns.map((column) => {
        return {
          ...column,
          render: function renderPlaceholder() {
            return (
              <Skeleton
                key={column.key}
                title
                active={active}
                paragraph={false}
                className={className}
              />
            );
          },
        };
      })}
    />
  ) : (
    <>
      {React.Children.map(children, (child) => hidePaginationWhenEmpty(child))}
    </>
  );
}
